import { useCallback, useEffect, useRef, useState } from "react";

const getRecognitionCtor = () => {
    if (typeof window === "undefined") return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export default function useSpeechRecognition() {
    const SR = getRecognitionCtor();
    const supported = !!SR;
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [error, setError] = useState(null);
    const recognitionRef = useRef(null);
    const onResultRef = useRef(null);

    useEffect(() => {
        if (!supported) return undefined;
        const r = new SR();
        r.lang = "en-US";
        r.continuous = false;
        r.interimResults = true;
        r.maxAlternatives = 1;
        recognitionRef.current = r;
        return () => {
            try {
                r.abort();
            } catch (err) {
                console.warn("SpeechRecognition abort failed:", err);
            }
        };
    }, [SR, supported]);

    const start = useCallback(
        (onResult) => {
            setError(null);
            setTranscript("");
            onResultRef.current = onResult || null;
            if (!supported || !recognitionRef.current) {
                setError("unsupported");
                onResultRef.current?.(null, "unsupported");
                return;
            }
            const r = recognitionRef.current;
            r.onstart = () => setIsListening(true);
            r.onend = () => setIsListening(false);
            r.onerror = (e) => {
                setError(e.error || "error");
                setIsListening(false);
                onResultRef.current?.(null, e.error || "error");
            };
            r.onresult = (e) => {
                let final = "";
                let interim = "";
                for (let i = e.resultIndex; i < e.results.length; i++) {
                    const txt = e.results[i][0].transcript;
                    if (e.results[i].isFinal) final += txt;
                    else interim += txt;
                }
                const current = (final || interim).trim();
                setTranscript(current);
                if (final && onResultRef.current) {
                    onResultRef.current(final.trim(), null);
                }
            };
            try {
                r.start();
            } catch (err) {
                console.warn("SpeechRecognition start failed (likely already started):", err);
            }
        },
        [supported],
    );

    const stop = useCallback(() => {
        try {
            recognitionRef.current?.stop();
        } catch (err) {
            console.warn("SpeechRecognition stop failed:", err);
        }
        setIsListening(false);
    }, []);

    return { start, stop, isListening, transcript, error, supported };
}
